import React, { useState, useCallback, useMemo } from 'react';
import type { TripResponseArgs } from '@this/domain/trip/trip2';
import { MESSAGE_TYPES } from '../../types/messages';
import type { ChatMessage, FeedbackType } from '../../types/messages';
import type { UserInfo } from '../../types/user';
import type { ProposalBundle } from '../../types/proposalBundles';
import type {
  OnProposalBundleClose,
  OnProposalBundleFeedbackUpdate,
  OnProposalItemSelect
} from '../../types/proposalHandlers';
import type { ReservationInfo } from '../../api/reservationInfo';
import AiAvatar from '../icons/AiAvatar';
import UserAvatar from '../icons/UserAvatar';
import OptimisticMessage from './OptimisticMessage';
import ProposalSection from '../proposal/ProposalSection';
import ProposalSummaryMessage from './ProposalSummaryMessage';
import ReservationInfoForm from './reservation_form/ReservationInfoForm';
import type { FormValues } from '../../types/reservationInfoForm';
import ArrangementConfirmationMessage from './ArrangementConfirmationMessage';
import PaymentFormMessage from './PaymentFormMessage';
import type { PaymentFormState } from '../../types/payment';
import ArrangementCompletedMessage from './ArrangementCompletedMessage';
import SearchSupportInsightMessage from './SearchSupportInsightMessage';
import UnsupportedRequestMessage from './UnsupportedRequestMessage';
import CalendarConnectionPromptMessage from './CalendarConnectionPromptMessage';
import CalendarContextSummaryMessage from './CalendarContextSummaryMessage';
import AiMessageFeedback from './AiMessageFeedback';
import { AnimatedMessageRow } from '../AnimatedMessageRow';
import BlockerChip from '../input_support/BlockerChip';
import type { Blocker } from '../../types/conversationState';
import Markdown from '@this/components/shared/markdown/markdown';
import { filterMessagesForDisplay } from './utils';
import { formatTimestamp } from '../../utils/formatTimestamp';
import { useTravelRules } from '../../hooks/useTravelRules';

const FEEDBACK_ENABLED_TYPES = [MESSAGE_TYPES.MESSAGE] as const;

const shouldShowFeedback = (message: ChatMessage): boolean => {
  if (!message.type) return true;
  return (FEEDBACK_ENABLED_TYPES as readonly string[]).includes(message.type);
};

type ChipHintProps = {
  blockers: Blocker[];
  searchTargets: string[];
  onChipClick: (template: string) => void;
};

interface Props {
  trip: TripResponseArgs;
  user?: UserInfo;
  messages: ChatMessage[];
  proposalBundles?: ProposalBundle[];
  reservationInfo?: ReservationInfo | null;
  onSelectItem?: OnProposalItemSelect;
  onCloseItem?: OnProposalBundleClose;
  onBundleFeedbackUpdate?: OnProposalBundleFeedbackUpdate;
  onSubmitReservationInfo?: (values: FormValues) => void;
  isSubmittingReservationInfo?: boolean;
  onArrangementRequest?: () => void;
  isRequestingArrangement?: boolean;
  isArrangementRequested?: boolean;
  isPaymentInvalid?: boolean;
  proposalChangeDisabled?: boolean;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  onCalendarDateSelect?: (dateText: string) => void;
  chipHint?: ChipHintProps;
  paymentFormValue?: PaymentFormState;
  onPaymentFormChange?: (next: PaymentFormState) => void;
  isSpLayout?: boolean;
}

const getUserInitials = (user?: UserInfo): string => {
  if (!user) return 'YOU';
  if (user.name) {
    const name = user.name.trim();
    if (name.length > 0) {
      return name.slice(0, 1);
    }
  }
  return 'YOU';
};

const MessageSection = ({
  trip,
  user,
  messages,
  proposalBundles = [],
  reservationInfo,
  onSelectItem,
  onCloseItem,
  onBundleFeedbackUpdate,
  onSubmitReservationInfo,
  isSubmittingReservationInfo = false,
  onArrangementRequest,
  isRequestingArrangement = false,
  isArrangementRequested = false,
  isPaymentInvalid = false,
  proposalChangeDisabled = false,
  scrollContainerRef,
  onCalendarDateSelect,
  chipHint,
  paymentFormValue,
  onPaymentFormChange,
  isSpLayout = false
}: Props) => {
  const styles = useMemo(() => getStyles(isSpLayout), [isSpLayout]);
  const userInitials = getUserInitials(user);
  const displayMessages = useMemo(
    () => filterMessagesForDisplay(messages, proposalBundles),
    [messages, proposalBundles]
  );
  const [feedbackOverrides, setFeedbackOverrides] = useState<
    Record<string, { feedback: FeedbackType; commentSubmitted: boolean }>
  >({});

  const handleFeedbackUpdate = useCallback(
    (messageId: string, feedback: FeedbackType, commentSubmitted: boolean) => {
      setFeedbackOverrides(prev => ({ ...prev, [messageId]: { feedback, commentSubmitted } }));
    },
    []
  );

  const { travelRules } = useTravelRules();
  const includeTax = travelRules?.organizationRules.includeTax ?? true;

  const renderReservationInfoFormMessage = (message: ChatMessage) => {
    if (!reservationInfo || !onSubmitReservationInfo) return null;
    return (
      <AnimatedMessageRow key={message.id} isNew={message.isNew} isSpLayout={isSpLayout}>
        <div style={styles.avatar}>
          <AiAvatar />
        </div>
        <div style={styles.messageContentAi}>
          <div style={styles.aiBubble}>
            <Markdown markdownText={message.text} />
          </div>
          <div style={{ marginTop: '12px' }}>
            <ReservationInfoForm
              reservationInfo={reservationInfo}
              onSubmit={onSubmitReservationInfo}
              isSubmitting={isSubmittingReservationInfo}
              editable={message.editable !== false}
            />
          </div>
          <div style={styles.timestamp}>{formatTimestamp(message.createdAt)}</div>
        </div>
      </AnimatedMessageRow>
    );
  };

  const renderPaymentFormMessage = (message: ChatMessage) => {
    if (!reservationInfo || !paymentFormValue || !onPaymentFormChange) return null;
    return (
      <AnimatedMessageRow key={message.id} isNew={message.isNew} isSpLayout={isSpLayout}>
        <div style={styles.avatar}>
          <AiAvatar />
        </div>
        <div style={styles.messageContentAi}>
          <div style={styles.aiBubble}>
            <Markdown markdownText={message.text} />
          </div>
          <div style={{ marginTop: '12px' }}>
            <PaymentFormMessage
              payment={reservationInfo.payment}
              value={paymentFormValue}
              onChange={onPaymentFormChange}
              disabled={isArrangementRequested}
            />
          </div>
          <div style={styles.timestamp}>{formatTimestamp(message.createdAt)}</div>
        </div>
      </AnimatedMessageRow>
    );
  };

  const getProposalBundlesForMessage = (message: ChatMessage) => {
    const bundleIds = message.proposalBundleIds;
    if (bundleIds == null) {
      return proposalBundles;
    }

    return proposalBundles.filter(bundle => bundleIds.includes(bundle.id));
  };

  const renderProposalMessage = (message: ChatMessage) => (
    <ProposalSection
      key={message.id}
      message={message}
      bundles={getProposalBundlesForMessage(message)}
      tripId={trip.id}
      includeTax={includeTax}
      disabled={proposalChangeDisabled}
      onSelectItem={onSelectItem}
      onCloseItem={onCloseItem}
      onBundleFeedbackUpdate={onBundleFeedbackUpdate}
      isSpLayout={isSpLayout}
    />
  );

  const renderOptimisticMessage = (message: ChatMessage) => (
    <AnimatedMessageRow key={message.id} isNew={message.isNew} isSpLayout={isSpLayout}>
      <div style={styles.avatar}>
        <AiAvatar />
      </div>
      <div style={styles.messageContentAi}>
        <OptimisticMessage text={message.text} />
      </div>
    </AnimatedMessageRow>
  );

  const renderSummaryMessage = (message: ChatMessage) => {
    if (!message.summary) return null;
    return (
      <AnimatedMessageRow key={message.id} isNew={message.isNew} isSpLayout={isSpLayout}>
        <div style={styles.avatar}>
          <AiAvatar />
        </div>
        <div style={styles.messageContentAi}>
          <div style={styles.aiBubble}>
            <ProposalSummaryMessage text={message.text} summary={message.summary} includeTax={includeTax} />
          </div>
          <div style={styles.timestamp}>{formatTimestamp(message.createdAt)}</div>
        </div>
      </AnimatedMessageRow>
    );
  };

  const renderArrangementConfirmationMessage = (message: ChatMessage) => {
    if (!message.summary || !onArrangementRequest) return null;
    return (
      <AnimatedMessageRow key={message.id} isNew={message.isNew} isSpLayout={isSpLayout}>
        <div style={styles.avatar}>
          <AiAvatar />
        </div>
        <div style={styles.messageContentAi}>
          <ArrangementConfirmationMessage
            tripId={String(trip.id)}
            text={message.text}
            summary={message.summary}
            includeTax={includeTax}
            onArrangementRequest={onArrangementRequest}
            isRequesting={isRequestingArrangement}
            isRequested={isArrangementRequested}
            isPaymentInvalid={isPaymentInvalid}
          />
          <div style={styles.timestamp}>{formatTimestamp(message.createdAt)}</div>
        </div>
      </AnimatedMessageRow>
    );
  };

  const renderArrangementCompletedMessage = (message: ChatMessage) => {
    return (
      <AnimatedMessageRow key={message.id} isNew={message.isNew} isSpLayout={isSpLayout}>
        <div style={styles.avatar}>
          <AiAvatar />
        </div>
        <div style={styles.messageContentAi}>
          <ArrangementCompletedMessage
            text={message.text}
            tripId={trip.id}
            isNewlyCompleted={message.isNewlyCompleted ?? false}
          />
          <div style={styles.timestamp}>{formatTimestamp(message.createdAt)}</div>
        </div>
      </AnimatedMessageRow>
    );
  };

  const renderSearchSupportInsightMessage = (message: ChatMessage) => {
    if (!message.insight) return null;
    return (
      <AnimatedMessageRow key={message.id} isNew={message.isNew} isSpLayout={isSpLayout}>
        <div style={styles.avatar}>
          <AiAvatar />
        </div>
        <div style={styles.messageContentAiProposal}>
          <SearchSupportInsightMessage text={message.text} insight={message.insight} />
          <div style={styles.timestamp}>{formatTimestamp(message.createdAt)}</div>
        </div>
      </AnimatedMessageRow>
    );
  };

  const renderUnsupportedRequestMessage = (message: ChatMessage) => {
    if (!message.unsupportedRequest) return null;
    return (
      <AnimatedMessageRow key={message.id} isNew={message.isNew} isSpLayout={isSpLayout}>
        <div style={styles.avatar}>
          <AiAvatar />
        </div>
        <div style={styles.messageContentAi}>
          <UnsupportedRequestMessage unsupportedRequest={message.unsupportedRequest} />
          <div style={styles.timestamp}>{formatTimestamp(message.createdAt)}</div>
        </div>
      </AnimatedMessageRow>
    );
  };

  const renderCalendarConnectionPromptMessage = (message: ChatMessage) => {
    if (!message.calendarConnectionPrompt) return null;
    return (
      <AnimatedMessageRow key={message.id} isNew={message.isNew} isSpLayout={isSpLayout}>
        <div style={styles.avatar}>
          <AiAvatar />
        </div>
        <div style={styles.messageContentAi}>
          <CalendarConnectionPromptMessage prompt={message.calendarConnectionPrompt} />
          <div style={styles.timestamp}>{formatTimestamp(message.createdAt)}</div>
        </div>
      </AnimatedMessageRow>
    );
  };

  const renderCalendarContextSummaryMessage = (message: ChatMessage) => {
    if (!message.calendarContextSummary) return null;
    return (
      <AnimatedMessageRow key={message.id} isNew={message.isNew} isSpLayout={isSpLayout}>
        <div style={styles.avatar}>
          <AiAvatar />
        </div>
        <div style={isSpLayout ? styles.messageContentAiProposal : styles.messageContentAi}>
          <CalendarContextSummaryMessage
            text={message.text}
            summary={message.calendarContextSummary}
            onSelectDate={onCalendarDateSelect}
          />
          <div style={styles.timestamp}>{formatTimestamp(message.createdAt)}</div>
        </div>
      </AnimatedMessageRow>
    );
  };

  const renderChipHintMessage = (message: ChatMessage) => {
    const visibleBlockers = chipHint
      ? chipHint.blockers.filter(b => chipHint.searchTargets.includes(b.category))
      : [];

    return (
      <AnimatedMessageRow key={message.id} isNew={message.isNew} isSpLayout={isSpLayout}>
        <div style={styles.avatar}>
          <AiAvatar />
        </div>
        <div style={styles.messageContentAi}>
          <div style={{ ...styles.aiBubble, ...styles.chipHintBubble }}>
            <div>{message.text}</div>
            {chipHint && visibleBlockers.length > 0 && (
              <div style={styles.chipHintChips}>
                {visibleBlockers.map(blocker => (
                  <BlockerChip
                    key={`${blocker.key}-${blocker.type}`}
                    blocker={blocker}
                    onClick={chipHint.onChipClick}
                  />
                ))}
              </div>
            )}
          </div>
          <div style={styles.timestamp}>{formatTimestamp(message.createdAt)}</div>
        </div>
      </AnimatedMessageRow>
    );
  };

  const renderAiMessage = (message: ChatMessage) => {
    if (message.type === MESSAGE_TYPES.PROPOSAL) {
      return renderProposalMessage(message);
    }

    if (message.type === MESSAGE_TYPES.SUMMARY) {
      return renderSummaryMessage(message);
    }

    if (message.type === MESSAGE_TYPES.OPTIMISTIC) {
      return renderOptimisticMessage(message);
    }

    if (message.type === MESSAGE_TYPES.RESERVATION_INFO_FORM) {
      return renderReservationInfoFormMessage(message);
    }

    if (message.type === MESSAGE_TYPES.PAYMENT_FORM) {
      return renderPaymentFormMessage(message);
    }

    if (message.type === MESSAGE_TYPES.ARRANGEMENT_CONFIRMATION) {
      return renderArrangementConfirmationMessage(message);
    }

    if (message.type === MESSAGE_TYPES.ARRANGEMENT_COMPLETED) {
      return renderArrangementCompletedMessage(message);
    }
    if (message.type === MESSAGE_TYPES.SEARCH_SUPPORT_INSIGHT) {
      return renderSearchSupportInsightMessage(message);
    }
    if (message.type === MESSAGE_TYPES.UNSUPPORTED_REQUEST) {
      return renderUnsupportedRequestMessage(message);
    }
    if (message.type === MESSAGE_TYPES.CALENDAR_CONNECTION_PROMPT) {
      return renderCalendarConnectionPromptMessage(message);
    }
    if (message.type === MESSAGE_TYPES.CALENDAR_CONTEXT_SUMMARY) {
      return renderCalendarContextSummaryMessage(message);
    }
    if (message.type === MESSAGE_TYPES.CHIP_HINT) {
      return renderChipHintMessage(message);
    }
    const override = feedbackOverrides[message.id];
    const currentFeedback = override?.feedback ?? message.feedback;
    const feedbackCommentSubmitted = override?.commentSubmitted ?? message.feedbackCommentSubmitted;
    return (
      <AnimatedMessageRow key={message.id} isNew={message.isNew} isSpLayout={isSpLayout}>
        <div style={styles.avatar}>
          <AiAvatar />
        </div>
        <div style={styles.messageContentAi}>
          <div style={styles.aiBubble}>
            <Markdown markdownText={message.text} openLinksNewTab />
          </div>
          {shouldShowFeedback(message) && (
            <AiMessageFeedback
              messageId={message.id}
              tripId={String(trip.id)}
              currentFeedback={currentFeedback}
              feedbackCommentSubmitted={feedbackCommentSubmitted}
              onFeedbackUpdate={handleFeedbackUpdate}
            />
          )}
          <div style={styles.timestamp}>{formatTimestamp(message.createdAt)}</div>
        </div>
      </AnimatedMessageRow>
    );
  };

  const renderUserMessage = (message: ChatMessage) => (
    <AnimatedMessageRow key={message.id} isNew={message.isNew} isSpLayout={isSpLayout} isUser>
      <div style={styles.messageContentUser}>
        <div style={styles.userBubble}>{message.text || trip?.final_destination || ''}</div>
        <div style={styles.timestamp}>{formatTimestamp(message.createdAt)}</div>
      </div>
      <div style={styles.avatar}>
        <UserAvatar initials={userInitials} />
      </div>
    </AnimatedMessageRow>
  );

  return (
    <div style={styles.messages} ref={scrollContainerRef}>
      {displayMessages.map(message =>
        message.direction === 'ai' ? renderAiMessage(message) : renderUserMessage(message)
      )}
    </div>
  );
};

const getStyles = (isSpLayout: boolean) => ({
  messages: {
    flex: 1,
    padding: isSpLayout ? '12px' : '24px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
    overflow: 'auto' as const,
    backgroundColor: '#ffffff'
  },
  avatar: {
    flex: '0 0 48px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  messageContentAi: {
    flex: 1,
    maxWidth: '70%',
    minWidth: 0
  },
  messageContentAiProposal: {
    flex: 1,
    minWidth: 0,
    maxWidth: '100%'
  },
  messageContentUser: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-end' as const,
    maxWidth: '70%',
    flex: 1
  },
  aiBubble: {
    padding: '12px 16px',
    borderRadius: '8px',
    backgroundColor: '#f5f5f5',
    display: 'inline-block' as const
  },
  chipHintBubble: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: '10px'
  },
  chipHintChips: {
    display: 'flex' as const,
    flexWrap: 'wrap' as const,
    gap: '8px'
  },
  userBubble: {
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #83b9e4',
    backgroundColor: '#e6f4fb',
    display: 'inline-block' as const
  },
  timestamp: {
    fontSize: '11px',
    color: '#7d7d7d',
    marginTop: '4px'
  }
});

export default MessageSection;
